import { Alert, Empty, Skeleton, Space } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface FeedbackStateProps {
  title: string;
  description: string;
  actions?: ReactNode;
}

export type LoadingStateVariant = "page" | "section" | "table" | "inline";

export function LoadingState({
  title = "Loading",
  description = "Preparing the latest authoritative information.",
  variant = "section",
}: Partial<FeedbackStateProps> & { variant?: LoadingStateVariant }) {
  const { t } = useTranslation();
  const rows = variant === "inline" ? 0 : variant === "table" ? 5 : variant === "page" ? 6 : 3;
  return (
    <section
      className={`feedback-card feedback-card--loading feedback-card--${variant}`}
      data-variant={variant}
      aria-live="polite"
      aria-busy="true"
    >
      <Space orientation="vertical" size={variant === "inline" ? 4 : 10} style={{ width: "100%" }}>
        <strong>{title === "Loading" ? t("bi.feedbackLoading", "Loading") : title}</strong>
        {variant === "inline" ? null : <span>{description === "Preparing the latest authoritative information." ? t("bi.feedbackLoadingDescription", "Preparing the latest authoritative information.") : description}</span>}
        {rows === 0 ? <span className="feedback-loading-dot" aria-hidden="true" /> : <Skeleton active title={variant !== "table"} paragraph={{ rows }} />}
      </Space>
    </section>
  );
}

export function EmptyState({ title = "No results", description = "No information is available for this view.", actions }: Partial<FeedbackStateProps>) {
  const { t } = useTranslation();
  return <section className="feedback-card"><Empty description={<><strong>{title === "No results" ? t("bi.feedbackNoResults", "No results") : title}</strong><div>{description === "No information is available for this view." ? t("bi.feedbackNoResultsDescription", "No information is available for this view.") : description}</div></>}>{actions}</Empty></section>;
}

export function UnavailableState({ title = "Unavailable", description = "The backend has not supplied this information.", actions }: Partial<FeedbackStateProps>) {
  const { t } = useTranslation();
  return <Alert showIcon type="warning" title={title === "Unavailable" ? t("bi.feedbackUnavailable", "Unavailable") : title} description={description === "The backend has not supplied this information." ? t("bi.feedbackUnavailableDescription", "The backend has not supplied this information.") : description} action={actions} />;
}

interface ErrorStateProps extends Partial<FeedbackStateProps> { technicalDetail?: string }

export function ErrorState({ title = "Unable to load this view", description = "Retry the read-only request.", actions, technicalDetail }: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <section className="feedback-card feedback-card--error" role="alert">
      <Alert
        showIcon
        type="error"
        title={title === "Unable to load this view" ? t("bi.feedbackError", "Unable to load this view") : title}
        description={description === "Retry the read-only request." ? t("bi.feedbackErrorDescription", "Retry the read-only request.") : description}
        action={actions}
      />
      {technicalDetail === undefined ? null : <details className="technical-detail"><summary>{t("bi.feedbackTechnicalDetail", "Technical detail")}</summary><code>{technicalDetail}</code></details>}
    </section>
  );
}

export function PartialCoverageState({ title = "Partial coverage", description = "Some backend facts remain missing or unresolved.", actions }: Partial<FeedbackStateProps>) {
  const { t } = useTranslation();
  return <Alert showIcon type="warning" title={title === "Partial coverage" ? t("bi.feedbackPartialCoverage", "Partial coverage") : title} description={description === "Some backend facts remain missing or unresolved." ? t("bi.feedbackPartialCoverageDescription", "Some backend facts remain missing or unresolved.") : description} action={actions} />;
}

export type RefreshFeedbackState = "refreshing" | "updated" | "noop" | "partial" | "failed";

export function RefreshFeedback({ state, label }: { state: RefreshFeedbackState; label?: string }) {
  const { t } = useTranslation();
  const defaultLabel: Record<RefreshFeedbackState, string> = {
    refreshing: t("bi.refreshRefreshing", "Refreshing…"),
    updated: t("bi.refreshUpdated", "Updated just now"),
    noop: t("bi.refreshNoop", "Already up to date"),
    partial: t("bi.refreshPartial", "Updated with partial coverage"),
    failed: t("bi.refreshFailed", "Refresh failed"),
  };
  return (
    <span
      className={`refresh-feedback refresh-feedback--${state}`}
      role={state === "failed" ? "alert" : "status"}
      aria-live={state === "failed" ? "assertive" : "polite"}
    >
      <span className="refresh-feedback__signal" aria-hidden="true" />
      {label ?? defaultLabel[state]}
    </span>
  );
}
